/**
 * useCalendarUIState Hook
 * 
 * Purpose:
 * Custom hook for managing all UI-only state in the calendar page, including modal states,
 * date selection, mobile/responsive UI toggles, calendar navigation, display filters, and
 * UI-related refs. This hook handles only presentation-layer state and does not manage data
 * fetching or business logic calculations.
 * 
 * High-level Workflow:
 * 1. Initialize UI state: Modal states, date selection, mobile detection, calendar navigation,
 *    display filters, and visual states (hero image, docking, collapse).
 * 2. Mobile detection: Sets up window resize listener to detect mobile viewport (<=768px).
 * 3. Scroll docking: Sets up IntersectionObserver to detect when header should dock on scroll.
 * 4. Performance tracking: Records mount time and performance marks for debugging.
 * 5. Hero image preloading: Preloads property hero image and tracks loading state.
 * 6. LocalStorage persistence: Persists calendar view mode and collapse state per property.
 * 7. Event handlers: Provides handlers for modal open/close, date selection, month navigation,
 *    and calendar collapse/expand.
 * 
 * Assumptions & Invariants:
 * - UI-only state: This hook manages only presentation-layer state. Data fetching, business
 *   logic, and data transformations are handled elsewhere (data fetching hooks, useMemo in page).
 * - Mobile breakpoint: Mobile detection uses window.innerWidth <= 768px. This breakpoint is
 *   assumed to match CSS media queries and design system breakpoints.
 * - LocalStorage keys: Uses keys "owner.calendar.view" (view mode) and
 *   "owner.calendar.collapsed.{propertyId}" (collapse state per property).
 * - Calendar month navigation: Changing calendarMonth triggers data fetches (via dependency
 *   in data fetching hooks), but the navigation itself is UI-only (user interaction).
 * - Event filter: eventFilter only affects display (which events show in upcoming events),
 *   not data fetching. Data is fetched regardless of filter state.
 * - Modal state: Modal open/close state is independent of data. Modals display data but
 *   don't trigger fetches.
 * - Performance refs: mountTimeRef, heroImageRef, dockSentinelRef, gridRenderTimeRef are
 *   used for performance tracking, image preloading, and scroll detection (UI concerns).
 * 
 * Dependencies:
 * - Internal:
 *   - None (pure UI state management)
 * - External:
 *   - react: useState, useEffect, useRef, useMemo
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Initial extraction from CalendarPage.jsx (Phase 3 refactoring)
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useViewport } from '../contexts/ViewportContext.jsx';

// Performance debug flag (from env or URL param)
const PERF_DEBUG = typeof window !== 'undefined' && (
  import.meta.env.VITE_OWNER_PERF_DEBUG === '1' ||
  new URLSearchParams(window.location.search).get('perf_debug') === '1'
);

/**
 * Hook for managing calendar page UI state
 * 
 * @param {string|null} selectedPropertyId - Selected property ID (for localStorage keys)
 * @param {string|null} propertyHero - Property hero image URL (for preloading)
 * @returns {Object} UI state and handlers
 */
export function useCalendarUIState({ selectedPropertyId, propertyHero }) {
  // Modal states
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPropertyOverviewOpen, setIsPropertyOverviewOpen] = useState(false);

  // Date/selection states
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedWeekStart, setSelectedWeekStart] = useState(null);
  const [dayDetailsExpanded, setDayDetailsExpanded] = useState(true);

  // Mobile/responsive states (isMobile from shared ViewportContext; breakpoint 768)
  const { isMobile } = useViewport();
  const [calendarView, setCalendarView] = useState(() => {
    if (typeof window === 'undefined') return 'week'; // Default to week on mobile
    const stored = localStorage.getItem('owner.calendar.view');
    return stored === 'month' ? 'month' : 'week'; // Default to week
  });
  const [isKPIsExpanded, setIsKPIsExpanded] = useState(false);

  // Calendar navigation state
  const [calendarMonth, setCalendarMonth] = useState(() => {
    // Initialize to current month (first day)
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  // Display filters (UI-only, affects display not fetching)
  const [eventFilter, setEventFilter] = useState({ checkIn: true, checkOut: true });

  // UI visual states
  const [heroImageReady, setHeroImageReady] = useState(false);
  const [isDocked, setIsDocked] = useState(false);
  const [isCalendarCollapsed, setIsCalendarCollapsed] = useState(false);
  const [showPropertiesTimeout, setShowPropertiesTimeout] = useState(false);

  // UI refs
  const mountTimeRef = useRef(null);
  const heroImageRef = useRef(null);
  const dockSentinelRef = useRef(null);
  const gridRenderTimeRef = useRef(null);

  // Calendar collapse state with persistence per property
  const collapseKey = useMemo(() => `owner.calendar.collapsed.${selectedPropertyId || 'default'}`, [selectedPropertyId]);

  // Scroll-activated sticky: use IntersectionObserver to detect when header should dock
  useEffect(() => {
    if (!dockSentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // When sentinel is NOT intersecting (above viewport), dock the header
        setIsDocked(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '0px',
      }
    );

    observer.observe(dockSentinelRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Performance marks on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && window.performance) {
      mountTimeRef.current = performance.now();
      performance.mark('owner_calendar_mount');
      
      if (PERF_DEBUG) {
        console.log('[OWNER_PERF] Calendar page mounted', {
          timestamp: new Date().toISOString(),
          mountTime: mountTimeRef.current,
        });
      }
    }
  }, []);

  // Calendar view persistence (localStorage)
  useEffect(() => {
    if (typeof window !== 'undefined' && isMobile) {
      localStorage.setItem('owner.calendar.view', calendarView);
    }
  }, [calendarView, isMobile]);

  // Update collapse state when property changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = localStorage.getItem(collapseKey);
    setIsCalendarCollapsed(stored === 'true');
  }, [collapseKey]);

  // Preload hero image (non-blocking)
  useEffect(() => {
    if (!propertyHero) {
      setHeroImageReady(false);
      return;
    }
    
    // Reset state
    setHeroImageReady(false);
    
    // Preload image
    const img = new Image();
    img.onload = () => {
      setHeroImageReady(true);
      if (PERF_DEBUG) {
        console.log('[OWNER_PERF] Hero image loaded', {
          url: propertyHero,
          loadTime: mountTimeRef.current ? performance.now() - mountTimeRef.current : null,
        });
      }
    };
    img.onerror = () => {
      if (PERF_DEBUG) {
        console.warn('[OWNER_PERF] Hero image failed to load', {
          url: propertyHero,
        });
      }
      // Keep fallback gradient
    };
    img.src = propertyHero;
    heroImageRef.current = img;
    
    return () => {
      // Cleanup: abort image load if component unmounts
      if (heroImageRef.current) {
        heroImageRef.current.onload = null;
        heroImageRef.current.onerror = null;
      }
    };
  }, [propertyHero]);

  // UI Handlers
  const handleBookingClick = (booking) => {
    // TEMP DEBUG: Gated by ?perf_debug=1 - Log booking object from calendar click
    const perfDebug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf_debug') === '1';
    
    if (perfDebug) {
      console.log('[Calendar click] booking object:', booking);
      console.log('[Calendar click] booking keys:', Object.keys(booking || {}));
      console.log('[Calendar click] booking identifiers:', {
        reservation_id: booking?.reservation_id,
        hostaway_reservation_id: booking?.hostaway_reservation_id,
        id: booking?.id,
        external_id: booking?.external_id,
        channel_code: booking?.channel_code,
        guest_name: booking?.guest_name,
      });
    }
    
    setSelectedBooking(booking);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBooking(null);
  };

  // PHASE 8: Enhanced date click handler - compute week and show week strip
  const handleDateClick = (date, dateKey) => {
    if (!date || !isMobile) {
      setSelectedDate(date);
      return;
    }
    
    // PHASE 8: Compute week start (Sunday) for the selected date
    const clickedDate = date instanceof Date ? date : new Date(date);
    const dayOfWeek = clickedDate.getDay(); // 0 = Sunday, 6 = Saturday
    const weekStart = new Date(clickedDate);
    weekStart.setDate(clickedDate.getDate() - dayOfWeek);
    weekStart.setHours(0, 0, 0, 0);
    
    setSelectedDate(clickedDate);
    setSelectedWeekStart(weekStart);
    setDayDetailsExpanded(true);
  };

  const toggleCalendarCollapse = () => {
    const newState = !isCalendarCollapsed;
    setIsCalendarCollapsed(newState);
    if (typeof window !== 'undefined') {
      localStorage.setItem(collapseKey, String(newState));
    }
  };

  // PHASE 5: Month navigation handlers for mobile command bar
  const handlePrevMonth = () => {
    const year = calendarMonth.getFullYear();
    const monthIndex = calendarMonth.getMonth();
    const newDate = new Date(year, monthIndex - 1, 1);
    setCalendarMonth(newDate);
  };

  const handleNextMonth = () => {
    const year = calendarMonth.getFullYear();
    const monthIndex = calendarMonth.getMonth();
    const newDate = new Date(year, monthIndex + 1, 1);
    setCalendarMonth(newDate);
  };

  // PHASE 11: Today button handler
  const handleToday = () => {
    const today = new Date();
    setCalendarMonth(today);
    // Also clear selected date to show current month view
    setSelectedDate(null);
    setSelectedWeekStart(null);
  };

  // Test hook: Allow programmatic modal opening for E2E tests
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleTestOpenModal = (event) => {
        if (event.detail && event.detail.booking) {
          handleBookingClick(event.detail.booking);
        }
      };
      window.addEventListener('test-open-booking-modal', handleTestOpenModal);
      return () => {
        window.removeEventListener('test-open-booking-modal', handleTestOpenModal);
      };
    }
  }, []); // Empty deps - handler is stable

  return {
    // Modal states
    selectedBooking,
    setSelectedBooking,
    isModalOpen,
    setIsModalOpen,
    isPropertyOverviewOpen,
    setIsPropertyOverviewOpen,
    handleBookingClick,
    handleCloseModal,

    // Date/selection states
    selectedDate,
    setSelectedDate,
    selectedWeekStart,
    setSelectedWeekStart,
    dayDetailsExpanded,
    setDayDetailsExpanded,
    handleDateClick,

    // Mobile/responsive states
    isMobile,
    calendarView,
    setCalendarView,
    isKPIsExpanded,
    setIsKPIsExpanded,

    // Calendar navigation
    calendarMonth,
    setCalendarMonth,
    handlePrevMonth,
    handleNextMonth,
    handleToday,

    // Display filters
    eventFilter,
    setEventFilter,

    // UI visual states
    heroImageReady,
    setHeroImageReady,
    isDocked,
    setIsDocked,
    isCalendarCollapsed,
    setIsCalendarCollapsed,
    toggleCalendarCollapse,
    showPropertiesTimeout,
    setShowPropertiesTimeout,

    // UI refs
    mountTimeRef,
    heroImageRef,
    dockSentinelRef,
    gridRenderTimeRef,
  };
}
